import React, { useState, forwardRef, useRef, useEffect } from "react";
import clsx from "clsx";

import { makeStyles, alpha } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";

import { colors } from "src/theme";
import { formatDollars } from "src/utils";

import { StatementDrillSalesTable } from "./StatementDrill";
import { SalesTaxReport as SalesTaxReportType } from "./report";
import Title, { DownloadButton } from "./Title";
import CompanyInfo from "./CompanyInfo";
import { render as pdfRender } from "./pdf";

const StatementDrillDownTooltipText = "Click to view details";

type StatementDrillTypeWithReport = { state: string } | null;

function getSortedKeys(obj: Record<string, any>): string[] {
  const keys = Object.keys(obj);
  keys.sort((a, b) => a.localeCompare(b));

  return keys;
}

const useBodyStyles = makeStyles((theme) => ({
  table: {
    margin: theme.spacing(3, "auto", 4),
    maxWidth: 480,
    width: "100%",
    borderSpacing: 0,
    borderCollapse: "collapse",
    "& td:first-child": {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
      paddingRight: theme.spacing(2),
    },
    "& td:last-child": {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
    "& td": {
      borderRadius: ({ isCompare }: { isCompare: boolean }) =>
        isCompare ? theme.shape.borderRadius : 0,
    },
  },
  left: {
    textAlign: "left",
  },
  right: {
    textAlign: "right",
  },
  bold: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  detail: {
    paddingLeft: theme.spacing(2),
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  blue: {
    color: colors.blueColor,
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  space: {
    padding: theme.spacing(0, 2),
  },
  pointer: {
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  highlightedRow: {
    "& td": {
      background: alpha(theme.palette.primary.main, 0.15),
    },
    "&:hover td": {
      background: alpha(theme.palette.primary.main, 0.25),
    },
  },
  highlightedCell: {
    background: alpha(theme.palette.primary.main, 0.15),
    "&:hover": {
      background: alpha(theme.palette.primary.main, 0.25),
    },
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
}));

interface SalesTaxReportBodyProps {
  report: SalesTaxReportType;
  onStatementDrillChange: (st: StatementDrillTypeWithReport) => void;
  statementDrill: StatementDrillTypeWithReport;
}

const SalesTaxReportBody = forwardRef(
  (
    { report, onStatementDrillChange, statementDrill }: SalesTaxReportBodyProps,
    ref
  ) => {
    const classes = useBodyStyles({ isCompare: false });

    return (
      <Box className={classes.table} {...{ ref }} component="table">
        <thead>
          <tr>
            <th className={clsx(classes.bold, classes.left)}>Sale State</th>
            <th className={clsx(classes.bold, classes.right)}>Liable to Pay</th>
            <th className={clsx(classes.bold, classes.right)}>
              Not Liable to Pay
            </th>
            <th className={clsx(classes.bold, classes.right)}>Total</th>
          </tr>
        </thead>
        <tbody>
          {getSortedKeys(report.data.taxByState).map((state) => {
            const value = report.data.taxByState[state];
            const selectedRow = statementDrill?.state === state;
            return (
              <Tooltip
                key={state}
                title={StatementDrillDownTooltipText}
                placement="top"
              >
                <tr
                  className={clsx(
                    classes.pointer,
                    selectedRow && classes.highlightedRow
                  )}
                  onClick={(e) => {
                    onStatementDrillChange(
                      selectedRow
                        ? null
                        : {
                            state,
                          }
                    );
                  }}
                >
                  <td className={classes.detail}>{state}</td>
                  <td className={classes.right}>
                    {formatDollars(value.liableToPay)}
                  </td>
                  <td className={classes.right}>
                    {formatDollars(value.nonLiableToPay)}
                  </td>
                  <td className={classes.right}>
                    {formatDollars(value.liableToPay + value.nonLiableToPay)}
                  </td>
                </tr>
              </Tooltip>
            );
          })}
        </tbody>
      </Box>
    );
  }
);

export interface SalesTaxReportProps {
  report: SalesTaxReportType;
}

function StatementDrill({ type, salesByState }) {
  if (!type) return null;

  return (
    <Box mt={2}>
      <h2 className="text-blue">Sales by State - {type.state}</h2>
      <Box height="75vh">
        <StatementDrillSalesTable rows={salesByState[type.state] || []} />
      </Box>
    </Box>
  );
}

function SalesTaxReport({ report }: SalesTaxReportProps) {
  const [statementDrill, setStatementDrill] =
    useState<StatementDrillTypeWithReport>(null);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    setStatementDrill(null);
  }, [report.data.reportDetails.titleFull]);

  const handleDownloadReport = async () => {
    if (!ref?.current) return;
    const filename = report.data.reportDetails.filename;
    await pdfRender(ref.current, {
      filename,
      onclone: (doc) => {
        const toolbar = doc.querySelector(".MuiDataGrid-root > div");
        if (toolbar) toolbar.remove();
      },
    });
  };

  return (
    <Paper className="mt-5 p-5 h-auto" ref={ref}>
      <CompanyInfo />
      <Title actions={<DownloadButton onClick={handleDownloadReport} />}>
        {report.data.reportDetails.titleFull}
      </Title>
      <SalesTaxReportBody
        report={report}
        statementDrill={statementDrill}
        onStatementDrillChange={(st) => {
          setStatementDrill(st);
        }}
      />
      <StatementDrill
        type={statementDrill}
        salesByState={report.data.salesByState}
      />
    </Paper>
  );
}

export default SalesTaxReport;
